<template>
  <Content
    class="col-span-full"
    :title="$route.name"
    :router="{ name: 'FINES_LIST' }"
  >
    <el-form
      status-icon
      :model="ruleForms[0]"
      :rules="rules"
      label-position="top"
      ref="ruleForm"
      label-width="120px"
      class="demo-ruleForm gap-8 flex flex-col"
    >
      <div
        v-for="(formEntry, index) in ruleForms"
        :key="index"
        class="
          form-entry
          grid grid-cols-1
          gap-4
          md:grid-cols-2
          lg:grid-cols-3
          xl:grid-cols-5
        "
      >
        <VehicleSelect
          v-if="index === 0"
          prop="company_vehicle"
          :model="formEntry.company_vehicle"
          @update="formEntry.company_vehicle = $event"
          :disabled="index > 0 || formEntry.disabled"
        />

        <el-form-item
          v-if="index === 0"
          :label="$t('DRIVER_TITLE')"
          prop="driver"
          v-loading="driverLoading"
          element-loading-spinner="el-icon-loading"
        >
          <el-select
            :disabled="index > 0 || formEntry.disabled"
            :placeholder="$t('SELECT_PLACEHOLDER')"
            v-model="formEntry.driver"
            clearable
            filterable
          >
            <el-option
              v-for="(item, idx) in driversList"
              :key="idx"
              :label="item.first_name + ' ' + item.last_name"
              :value="item.id || null"
            />
          </el-select>
        </el-form-item>

        <el-form-item v-if="index === 0" :label="$t('DATE_')" prop="date">
          <date-picker
            :disabled="formEntry.disabled"
            class="el-input__inner"
            v-model="formEntry.date"
            :locale="$i18n.locale"
            format="jYYYY-jMM-jDD"
            clearable
          />
        </el-form-item>

        <div
          v-if="index === 0 && !accident"
          class="lg:col-span-2 justify-end flex items-center"
        >
          <el-button type="primary" rounded @click="addEntry">
            <i class="el-icon-plus font-medium"></i>
          </el-button>
        </div>

        <el-form-item
          :label="$t('TYPE')"
          prop="type"
          :rules="{
            required: true,
            message: $t('REQUIRED_FIELD', {
              type: $t('TYPE'),
            }),
            trigger: 'change',
          }"
        >
          <el-select
            v-model="formEntry.type"
            :disabled="formEntry.disabled"
            clearable
            filterable
            :placeholder="$t('SELECT_PLACEHOLDER')"
          >
            <el-option
              v-for="item in typeOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>

        <!-- Cost Input -->
        <PriceInput
          label="PRICE"
          prop="cost"
          :loading="false"
          :model="formEntry.cost"
          @update="formEntry.cost = $event"
          :disabled="formEntry.disabled"
        />

        <el-form-item
          :label="$t('IS_DRIVER_AT_FAULT')"
          class="custom__swicth flex"
          prop="is_driver_at_fault"
        >
          <el-switch
            v-model="formEntry.is_driver_at_fault"
            :disabled="formEntry.disabled"
            active-text="بله"
            inactive-text="خیر"
          />
        </el-form-item>
        <div
          class="flex items-start justify-end lg:col-span-2 gap-2"
          v-if="addable"
        >
          <el-button
            type="danger"
            icon="el-icon-minus"
            rounded
            @click="removeEntry"
            v-if="ruleForms.length > 1"
          >
          </el-button>
        </div>
      </div>

      <!-- Submit and Reset Buttons -->
      <FormConfirmation
        v-show="!ruleForms[0].disabled"
        @resetForm="resetForm('ruleForm')"
        @submitForm="submitForm"
        :edit="null"
        :loading="submitLoading"
      />
    </el-form>
  </Content>
</template>

<script>
import { resetForm } from "@/mixins/resetForm";
import FormConfirmation from "@/components/elements/formConfirmation.vue";
import { addFine, editFine, getFineTypes } from "@/api/fines";
import PriceInput from "@/components/elements/priceInput.vue";
import { reverseCurrency } from "@/mixins/reverseCurrency";
import VehicleSelect from "@/components/elements/vehiclesSelect.vue";
import { driverSummary } from "@/api/drivers";
import { jalaliToGregorian } from "@/mixins/jalaliToGregorian";

export default {
  props: ["accident"],
  mixins: [reverseCurrency, jalaliToGregorian, resetForm],
  components: {
    FormConfirmation,
    PriceInput,
    VehicleSelect,
  },
  data() {
    return {
      submitLoading: false,
      driverLoading: true,
      addable: true,
      driversList: [],
      ruleForms: [
        {
          date: "",
          cost: "",
          is_driver_at_fault: true,
          type: [],
          company_vehicle: "",
          driver: "",
          disabled: false,
        },
      ],
      typeOptions: [],
      rules: {
        date: [
          {
            required: true,
            message: this.$i18n.t("REQUIRED_FIELD", {
              type: this.$i18n.t("DATE"),
            }),
            trigger: "change",
          },
        ],
        cost: [
          {
            required: true,
            trigger: "blur",
            message: this.$i18n.t("REQUIRED_FIELD", {
              type: this.$i18n.t("PRICE"),
            }),
          },
        ],
        is_driver_at_fault: [
          {
            required: true,
            message: this.$i18n.t("REQUIRED_FIELD", {
              type: this.$i18n.t("IS_DRIVER_AT_FAULT"),
            }),
            trigger: "change",
          },
        ],
        type: [
          {
            required: true,
            message: this.$i18n.t("REQUIRED_FIELD", {
              type: this.$i18n.t("TYPE"),
            }),
            trigger: "change",
          },
        ],
        company_vehicle: [
          {
            required: true,
            message: this.$i18n.t("REQUIRED_CHOOSE", {
              type: this.$i18n.t("VEHICLE_TITLE"),
            }),
            trigger: "blur",
          },
        ],
        driver: [
          {
            required: true,
            message: this.$i18n.t("REQUIRED_FIELD", {
              type: this.$i18n.t("DRIVER_TITLE"),
            }),
            trigger: "change",
          },
        ],
      },
    };
  },
  watch: {
    accident(val) {
      this.ruleForms[0] = val;
      this.addable = false;
    },
  },
  methods: {
    addEntry() {
      const newEntry = {
        date: "",
        cost: "",
        is_driver_at_fault: true,
        type: [],
        company_vehicle: this.ruleForms[0].company_vehicle,
        driver: this.ruleForms[0].driver,
      };
      this.ruleForms.push(newEntry);
    },
    removeEntry() {
      if (this.ruleForms.length > 1) {
        this.ruleForms.pop();
      }
    },
    submitForm() {
      this.$refs["ruleForm"].validate((valid) => {
        if (valid) {
          const addActionPayload = this.ruleForms.map((formEntry) => ({
            ...formEntry,
            cost: formEntry.cost ? +this.reverseCurrency(formEntry.cost) : null,
            date: this.ruleForms[0].date
              ? this.jalaliToGregorian(
                  this.ruleForms[0].date,
                  this.$i18n.locale
                )
              : null,
            company_vehicle: this.ruleForms[0].company_vehicle,
            driver: this.ruleForms[0].driver,
          }));

          const action = this.accident ? editFine : addFine;
          const editPayload = {
            ...this.ruleForms[0],
            cost: this.ruleForms[0].cost
              ? +this.reverseCurrency(this.ruleForms[0].cost)
              : null,
            date: this.ruleForms[0].date
              ? this.jalaliToGregorian(
                  this.ruleForms[0].date,
                  this.$i18n.locale
                )
              : null,
            company_vehicle: this.ruleForms[0].company_vehicle,
            driver: this.ruleForms[0].driver,
            id: this.accident ? this.accident.id : "",
          };

          const payload = this.accident ? editPayload : addActionPayload;

          this.submitLoading = true;
          action.api(payload).then((res) => {
            this.submitLoading = false;
            if (res) {
              this.$notify.success({
                title: `${this.$t(this.$route.name)} ${this.$t(
                  "SUCCESSFULLY_DONE"
                )}`,
                duration: 5000,
              });
              // reset form
              if (this.accident == null) {
                this.ruleForms = [
                  {
                    date: "",
                    cost: "",
                    is_driver_at_fault: true,
                    type: [],
                    company_vehicle: "",
                    driver: "",
                    disabled: false,
                  },
                ];
              } else
                this.$router.push({
                  name: "FINES_LIST",
                });
            } else {
              this.$notify.error({
                title: this.$i18n.t("FIXED_ERRORS", {
                  type: this.$t(this.$route.name),
                }),
                duration: 5000,
              });
            }
          });
        } else {
          this.$notify.error({
            title: this.$i18n.t("FIXED_ERRORS", {
              type: this.$i18n.t(this.$route.name),
            }),
            duration: 5000,
            customClass: "error_msg",
          });
          return false;
        }
      });
    },
  },
  mounted() {
    getFineTypes.api().then((res) => {
      if (res) {
        this.typeOptions = res.map((i) => ({ value: i.id, label: i.title }));
      }
    });

    driverSummary.api().then((res) => {
      if (res) {
        this.driverLoading = false;
        this.driversList = res;
      }
    });
  },
};
</script>
